import { useState } from 'react'

import {
  AccessCalendarSyncDocument,
  GetCalendarSyncsDocument,
  RevokeCalendarSyncDocument,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export const useRevokeAccess = () => {
  const [mustConfirm, setMustConfirm] = useState<boolean>(false)
  const [calendarSyncIdToRevoke, setCalendarSyncIdToRevoke] = useState<string | undefined>()

  const [revokeCalendar, { loading: processing }] = useMutation(RevokeCalendarSyncDocument, {
    extractorFn: (data) => data.cronofySchedulingMutations.revokeCronofyschedulingCalendarSync,
  })

  const applyChanges = async (id: string) => {
    const result = await revokeCalendar({
      variables: { id },
      refetchQueries: [GetCalendarSyncsDocument, AccessCalendarSyncDocument],
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('cronofy.revoke_calendar.success_message'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('cronofy.revoke_calendar.error_message'),
      })
    }

    setCalendarSyncIdToRevoke(undefined)
  }

  useEffect(
    () => {
      if (!!calendarSyncIdToRevoke && !mustConfirm) {
        applyChanges(calendarSyncIdToRevoke)
      }
    },
    [calendarSyncIdToRevoke, mustConfirm] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const cancelInvite = (id: string) => {
    setCalendarSyncIdToRevoke(id)
    setMustConfirm(false)
  }

  const revokeAccess = () => {
    setMustConfirm(false)
  }

  const openConfirmModal = (id: string) => {
    setMustConfirm(true)
    setCalendarSyncIdToRevoke(id)
  }

  const closeConfirmModal = () => {
    setCalendarSyncIdToRevoke(undefined)
    setMustConfirm(false)
  }

  return {
    mustConfirm,
    openConfirmModal,
    closeConfirmModal,
    cancelInvite,
    revokeAccess,
    processing,
  }
}
